import React from 'react';
import { ExternalLink, ArrowRight, Building, Store, Sprout, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import { oceanaPlatform } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

const ICONS = { Building, Store, Sprout, Package };

/**
 * Featured platform section for Smartmart subsidiary — showcases Océana.
 * Premium fruits & vegetables distribution in Côte d'Ivoire.
 */
const OceanaSection = () => {
  const { pick } = useLanguage();
  const c = oceanaPlatform.colors;

  return (
    <section
      className="section-padding-lg border-t relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0e1218 0%, #0a1410 100%)',
        borderColor: `${c.primary}40`
      }}
    >
      {/* Ambient green glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/4 w-[700px] h-[700px] rounded-full blur-3xl opacity-40"
          style={{ background: `radial-gradient(circle, ${c.primary}50 0%, transparent 70%)` }}
        />
        <div
          className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-30"
          style={{ background: `radial-gradient(circle, ${c.accent}50 0%, transparent 70%)` }}
        />
      </div>

      <div className="container-swg relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className="inline-block px-4 py-1.5 rounded-full text-[11px] tracking-[0.2em] uppercase mb-6 font-medium"
            style={{ color: c.accent, background: `${c.primary}25`, border: `1px solid ${c.primary}50` }}
            data-testid="oceana-eyebrow"
          >
            {pick(oceanaPlatform.eyebrow)}
          </span>

          <div className="flex items-center justify-center gap-5 mb-4 flex-wrap">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center p-3"
              style={{ background: c.cream, boxShadow: `0 0 30px ${c.primary}60` }}
            >
              <img
                src={oceanaPlatform.logo}
                alt="Océana"
                className="w-full h-full object-contain"
                data-testid="oceana-logo"
              />
            </div>
            <h2 className="text-5xl md:text-6xl font-serif tracking-tight text-white">
              Océana
            </h2>
          </div>
          <p className="text-xl md:text-2xl font-medium mt-3" style={{ color: c.accent }}>
            {pick(oceanaPlatform.tagline)}
          </p>
        </motion.div>

        {/* Brief + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <p className="text-lg text-white leading-relaxed mb-4">{pick(oceanaPlatform.shortBrief)}</p>
          <p className="text-[#9AACB8] leading-relaxed mb-10">{pick(oceanaPlatform.longBrief)}</p>

          <a
            href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
            className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-semibold tracking-wide uppercase text-sm transition-all duration-300 shadow-[0_8px_30px_rgba(0,118,104,0.4)] hover:shadow-[0_12px_40px_rgba(0,118,104,0.6)] hover:scale-[1.03]"
            style={{ background: c.primary, color: c.cream }}
            data-testid="oceana-cta"
          >
            {pick(oceanaPlatform.cta)}
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-3 gap-6 mb-16 max-w-3xl mx-auto"
          data-testid="oceana-stats"
        >
          {oceanaPlatform.stats.map((s) => (
            <div
              key={s.value}
              className="text-center p-6 rounded-2xl"
              style={{ background: `${c.primary}15`, border: `1px solid ${c.primary}30` }}
            >
              <div className="text-3xl md:text-4xl font-bold mb-1" style={{ color: c.accent }}>{s.value}</div>
              <div className="text-[#9AACB8] text-xs uppercase tracking-wider">{pick(s.label)}</div>
            </div>
          ))}
        </motion.div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {oceanaPlatform.services.map((service, index) => {
            const Icon = ICONS[service.icon] || Building;
            return (
              <motion.div
                key={service.icon}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="swg-card rounded-2xl p-6 border h-full"
                style={{ borderColor: `${c.primary}30` }}
                data-testid={`oceana-service-${service.icon.toLowerCase()}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${c.primary}25`, border: `1px solid ${c.primary}50` }}
                >
                  <Icon className="w-5 h-5" style={{ color: c.accent }} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2 leading-tight">{pick(service.name)}</h3>
                <p className="text-[#9AACB8] text-sm leading-relaxed">{pick(service.desc)}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Footer ribbon */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 flex items-center justify-center gap-3 text-xs text-[#9AACB8] tracking-[0.2em] uppercase"
        >
          <span>oceana-ci.com</span>
          <span>•</span>
          <span>Abidjan</span>
          <span>•</span>
          <a
            href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
            className="inline-flex items-center gap-1 transition-colors hover:underline"
            style={{ color: c.accent }}
          >
            Open site <ArrowRight className="w-3 h-3" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default OceanaSection;
